import {
  CARD_STATE,
  hasBillingToStop,
  hasNoSubscriptionYet,
  isCancelScheduled,
} from './listingCardState';

/**
 * Tone of a status row. Carried by an 8px dot — the text itself is never coloured,
 * so that a status reads as something to read rather than something to click.
 */
export const ROW_TONE = {
  POSITIVE: 'positive',
  WARNING: 'warning',
  NEGATIVE: 'negative',
  NEUTRAL: 'neutral',
};

const PREFIX = 'ListingStatusRows';

/**
 * The two status rows: what the advertisement is doing, and whether money is still
 * moving. Only two labels exist — the sale status lives in the photo badge.
 *
 * The subscription row deliberately carries the *consequential date* in its own
 * value ("er wordt nog afgeschreven op 12 mrt 2026") rather than deferring it to a
 * dialog. Whether they are still being charged is the question sellers open support
 * tickets about, so the card answers it without being opened.
 *
 * @param {Object} args
 * @param {string} args.state a CARD_STATE
 * @param {boolean} [args.isBusinessSeller] business sellers have no subscription row
 * @param {Object} [args.listingSubscription]
 * @returns {{ advertisement: Object, subscription: Object|null, noteId: string|null }}
 */
export const deriveStatusRows = ({ state, isBusinessSeller = false, listingSubscription }) => {
  const advertisement = advertisementRow(state);
  const subscription = isBusinessSeller ? null : subscriptionRow(state, listingSubscription);

  return {
    advertisement,
    subscription,
    noteId: noteFor(state),
  };
};

/**
 * Rows needing a date interpolate it. `expiresAt` is the end of the paid period —
 * the next charge while the subscription renews, and the day the advertisement comes
 * down once it is cancelled.
 */
const advertisementRow = state => {
  switch (state) {
    case CARD_STATE.BLANK:
      return { valueId: `${PREFIX}.ad.unfinished`, tone: ROW_TONE.NEUTRAL };
    case CARD_STATE.DRAFT:
    case CARD_STATE.DRAFT_PAID:
      return { valueId: `${PREFIX}.ad.draft`, tone: ROW_TONE.NEUTRAL };
    case CARD_STATE.ONLINE:
    case CARD_STATE.NEGOTIATION:
      return { valueId: `${PREFIX}.ad.online`, tone: ROW_TONE.POSITIVE };
    case CARD_STATE.CANCEL_SCHEDULED:
      return {
        valueId: `${PREFIX}.ad.onlineUntil`,
        tone: ROW_TONE.WARNING,
        dateField: 'expiresAt',
      };
    case CARD_STATE.OFFLINE:
      return { valueId: `${PREFIX}.ad.offline`, tone: ROW_TONE.NEUTRAL };
    case CARD_STATE.EXPIRED:
      return {
        valueId: `${PREFIX}.ad.offlineSince`,
        tone: ROW_TONE.NEGATIVE,
        dateField: 'expiresAt',
      };
    case CARD_STATE.SOLD:
      return { valueId: `${PREFIX}.ad.archived`, tone: ROW_TONE.NEUTRAL };
    case CARD_STATE.LOCKED:
    default:
      return { valueId: `${PREFIX}.ad.unavailable`, tone: ROW_TONE.NEUTRAL };
  }
};

const subscriptionRow = (state, listingSubscription) => {
  if (hasNoSubscriptionYet(state)) {
    return { valueId: `${PREFIX}.sub.notChosen`, tone: ROW_TONE.NEUTRAL };
  }

  // A pending cancellation outranks whatever else the state would say, in every state
  // that has a subscription — not only a published one.
  //
  // CARD_STATE.CANCEL_SCHEDULED is only reachable for a published listing, so a paid
  // *draft* whose subscription had been cancelled still read "Actief · volgende
  // afschrijving <date>": it named a charge that was never going to happen and said
  // nothing about the cancellation the seller had just made. The same applied to a
  // temporarily-offline advertisement.
  if (
    isCancelScheduled(listingSubscription) &&
    ![CARD_STATE.EXPIRED, CARD_STATE.SOLD].includes(state)
  ) {
    return {
      valueId: `${PREFIX}.sub.cancelledUntil`,
      tone: ROW_TONE.WARNING,
      dateField: 'expiresAt',
    };
  }

  switch (state) {
    // Billing started at payment, not at publication — so say so, with the date, or
    // the seller has no idea the clock is already running on an invisible draft.
    case CARD_STATE.DRAFT_PAID:
      return {
        valueId: `${PREFIX}.sub.activeSince`,
        tone: ROW_TONE.POSITIVE,
        dateField: 'expiresAt',
      };
    case CARD_STATE.ONLINE:
    case CARD_STATE.NEGOTIATION:
      return {
        valueId: `${PREFIX}.sub.activeNextCharge`,
        tone: ROW_TONE.POSITIVE,
        dateField: 'expiresAt',
        withPlan: true,
      };
    case CARD_STATE.CANCEL_SCHEDULED:
      return { valueId: `${PREFIX}.sub.cancelled`, tone: ROW_TONE.WARNING };
    // The card is grey, but money is still moving. This is precisely the support
    // question, so it is stated plainly rather than softened.
    case CARD_STATE.OFFLINE:
      return {
        valueId: `${PREFIX}.sub.activeStillCharged`,
        tone: ROW_TONE.WARNING,
        dateField: 'expiresAt',
      };
    case CARD_STATE.EXPIRED:
      return { valueId: `${PREFIX}.sub.expired`, tone: ROW_TONE.NEGATIVE };
    // Archiving an advertisement is not the same as stopping its subscription, and
    // this row used to assert that it was: every sold listing read "Opgezegd op
    // {date} · er wordt niets meer afgeschreven" without consulting the subscription
    // at all. A seller whose cancellation had failed was told the billing had stopped
    // — directly above the red strip saying it had not.
    //
    // The date was wrong too. `updatedAt` means "when this record last changed", and
    // archiving bumps it, so the row named the day the boat was marked sold and called
    // it the cancellation date. `expiresAt` is the end of the paid period: a real date
    // with one meaning.
    case CARD_STATE.SOLD:
      if (!listingSubscription) {
        return { valueId: `${PREFIX}.sub.notChosen`, tone: ROW_TONE.NEUTRAL };
      }
      if (hasBillingToStop(listingSubscription)) {
        return {
          valueId: `${PREFIX}.sub.activeStillCharged`,
          tone: ROW_TONE.WARNING,
          dateField: 'expiresAt',
        };
      }
      // Cancelled, lapsed, or already over — all of them mean the money has stopped,
      // and the terminal copy is the more useful thing to say about an archive than
      // the exact date the subscription winds down.
      return {
        valueId: `${PREFIX}.sub.cancelledOn`,
        tone: ROW_TONE.NEUTRAL,
        dateField: 'expiresAt',
      };
    case CARD_STATE.LOCKED:
    default:
      return listingSubscription
        ? { valueId: `${PREFIX}.sub.unknown`, tone: ROW_TONE.NEUTRAL }
        : { valueId: `${PREFIX}.sub.notChosen`, tone: ROW_TONE.NEUTRAL };
  }
};

/** A sentence, for consequences a row value cannot carry on its own. */
const noteFor = state => {
  switch (state) {
    case CARD_STATE.CANCEL_SCHEDULED:
      return `${PREFIX}.note.goesOfflineAfter`;
    case CARD_STATE.SOLD:
      return `${PREFIX}.note.archived`;
    default:
      return null;
  }
};
